import type {ReactElement, ReactNode} from 'react';
import type {ChatHistoryListItemOptionsProps} from './chat-history-list-item-options.types';
import {CHAT_HISTORY_LIST_ITEM_OPTIONS_PANEL_ID} from './chat-history-list-item-options.types';
import styles from './chat-history-list-item-options.module.scss';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Icon} from '@Components/icon-v2';
import {Dropdown} from '@Components/dropdown-v2';
import {ChatHistoryListItemOptionsPopup} from '@/ai-chat/components/chat-history-list/components/chat-history-list-item-options-popup/chat-history-list-item-options-popup';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import {closePanel} from '@Components/panel/panel-reducer';
import {useAppDispatch} from '@/hooks';
import {openConfirmDeleteModal} from '@Modals/confirm-delete-modal/confirm-delete-modal.types';

export function ChatHistoryListItemOptions({...props}: ChatHistoryListItemOptionsProps): ReactNode {
  const dispatch = useAppDispatch();

  const getSelector = (): ReactElement => {
    return (
      <Icon
        onClick={(e) => {
          e.stopPropagation();
        }}
        type={IconType.TRANSPARENT}
        size={IconSize.SIZE_ICON_16}
        shape={IconShape.SQUARE}
        icon={'icon-more-h'}
        className={styles.moreIcon}
        aria-label={window.i18next.t('pmwjs_chat_settings')}
      />
    );
  };

  const handleRename = (): void => {
    const isChatHistoryItemOptionsPanelOpen = !!window.PMW.redux.store.getState().panels.panelHashmap[CHAT_HISTORY_LIST_ITEM_OPTIONS_PANEL_ID];

    if (isChatHistoryItemOptionsPanelOpen) {
      dispatch(closePanel(CHAT_HISTORY_LIST_ITEM_OPTIONS_PANEL_ID));
    }

    props.onStartRename();
  };

  const handleDelete = (): void => {
    const isChatHistoryItemOptionsPanelOpen = !!window.PMW.redux.store.getState().panels.panelHashmap[CHAT_HISTORY_LIST_ITEM_OPTIONS_PANEL_ID];

    if (isChatHistoryItemOptionsPanelOpen) {
      dispatch(closePanel(CHAT_HISTORY_LIST_ITEM_OPTIONS_PANEL_ID));
    }

    openConfirmDeleteModal({
      onDeleteConfirmation: props.onDelete,
      text: window.i18next.t('pmwjs_delete_chat_permanently', {name: props.historyItem.title ?? window.i18next.t('pmwjs_new_chat')}),
      ctaText: window.i18next.t('pmwjs_delete'),
      title: window.i18next.t('pmwjs_delete_chat_modal_title'),
    });
  };

  const getPopup = (): ReactElement => {
    return <ChatHistoryListItemOptionsPopup onRename={handleRename} onDelete={handleDelete} />;
  };

  const getMoreOptionsDropdownForChatHistoryListItem = (): ReactNode => {
    return (
      <div
        className={styles.optionsWrapper}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <Dropdown
          onOpen={props.onOpen}
          onClose={props.onClose}
          key={CHAT_HISTORY_LIST_ITEM_OPTIONS_PANEL_ID}
          id={CHAT_HISTORY_LIST_ITEM_OPTIONS_PANEL_ID}
          doesCloseOnOuterClick
          doCloseOnInnerClick={true}
          showModalForSmallScreen
          panelIdForModal={CHAT_HISTORY_LIST_ITEM_OPTIONS_PANEL_ID}
          modalTitle={window.i18next.t('pmwjs_chat_settings')}
          selectorClassNameOnOpen={'-active'}
          selector={getSelector()}
          popup={getPopup()}
          className={styles.dropdown}
          alignment={ALIGNMENT_TYPE.RIGHT_START}
          position={DROPDOWN_POSITION.FIXED}
          popupClassName={styles.popup}
        />
      </div>
    );
  };

  return getMoreOptionsDropdownForChatHistoryListItem();
}
